'use client';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type {
  SetupStepState,
  StuckClinic,
  StuckClinicsReport,
} from '@borradh-workspace/api-client/types';
import { Link } from '@tanstack/react-router';
import { ExternalLink, PartyPopper, RefreshCw } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useRecheckSetupProgress, useStuckSetup } from '../../api/stuck-setup';

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });

const STEP_DOT: Record<SetupStepState['status'], string> = {
  done: 'bg-green-600 dark:bg-green-500',
  missing: 'bg-destructive',
  unknown: 'bg-muted-foreground/30',
};

/** One dot per setup step, in order: green done, red missing, grey unknown. */
function StepDots({ steps }: { steps: SetupStepState[] }) {
  return (
    <ol className="flex items-center gap-1" aria-label="Setup steps">
      {steps.map((s) => (
        <li
          key={s.step}
          title={`${s.label}: ${s.status}${s.detail ? ` (${s.detail})` : ''}`}
          aria-label={`${s.label}: ${s.status}`}
          className={cn('size-2.5 rounded-full', STEP_DOT[s.status])}
        />
      ))}
    </ol>
  );
}

function StuckAt({ clinic }: { clinic: StuckClinic }) {
  const step = clinic.steps.find((s) => s.step === clinic.firstMissingStep);
  return (
    <div className="flex max-w-md flex-col gap-0.5">
      <span className="font-medium">{clinic.firstMissingLabel}</span>
      {step?.detail ? (
        <span className="text-muted-foreground text-xs">{step.detail}</span>
      ) : null}
      <span className="flex flex-wrap items-center gap-x-3 text-xs">
        {step?.actionUrl ? (
          <a
            href={step.actionUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary inline-flex items-center gap-1 hover:underline"
          >
            {step.actionLabel ?? 'Fix it'}
            <ExternalLink className="size-3" />
          </a>
        ) : null}
        {clinic.daysOnStep !== null ? (
          <span className="text-muted-foreground">
            on this step {plural(clinic.daysOnStep, 'day', 'days')}
          </span>
        ) : null}
      </span>
    </div>
  );
}

function StuckTable({ clinics }: { clinics: StuckClinic[] }) {
  const { recheck, recheckingId } = useRecheckSetupProgress();
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Clinic</TableHead>
            <TableHead>Paying, no spend</TableHead>
            <TableHead>Stuck at</TableHead>
            <TableHead>Steps</TableHead>
            <TableHead>Meta checked</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clinics.map((clinic) => (
            <TableRow key={clinic.organizationId} className="align-top">
              <TableCell>
                <div className="flex flex-col items-start gap-1">
                  <Link
                    to="/admin/organizations/$id"
                    params={{ id: clinic.organizationId }}
                    className="text-primary font-medium hover:underline"
                  >
                    {clinic.organizationName}
                  </Link>
                  {clinic.subscriptionStatus === 'trialing' ? (
                    <Badge variant="outline">Trial</Badge>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <div className="font-medium">
                  {plural(clinic.daysPaying, 'day', 'days')}
                </div>
                <div className="text-muted-foreground text-xs">
                  since {shortDate(clinic.payingSince)}
                </div>
              </TableCell>
              <TableCell>
                <StuckAt clinic={clinic} />
              </TableCell>
              <TableCell>
                <StepDots steps={clinic.steps} />
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <div className="flex flex-col items-start gap-1">
                  <span className="text-muted-foreground text-xs">
                    {clinic.metaCheckedAt
                      ? shortDate(clinic.metaCheckedAt)
                      : 'Not yet'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    disabled={recheckingId === clinic.organizationId}
                    onClick={() => recheck(clinic.organizationId)}
                  >
                    <RefreshCw
                      className={cn(
                        recheckingId === clinic.organizationId && 'animate-spin'
                      )}
                    />
                    Check again
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function Header({
  minDays,
  onMinDays,
}: {
  minDays?: number;
  onMinDays?: (days: number | undefined) => void;
}) {
  const [draft, setDraft] = useState(minDays === undefined ? '' : `${minDays}`);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    // Empty = back to the API's default, not zero days.
    if (!draft.trim()) {
      onMinDays?.(undefined);
      return;
    }
    const days = Number(draft);
    if (Number.isInteger(days) && days >= 0 && days <= 365) onMinDays?.(days);
  };
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="max-w-3xl">
        <h1 className="text-3xl font-bold">Stuck in setup</h1>
        <p className="text-muted-foreground">
          Paying clinics (active or on trial) whose ads have never spent
          anything. A clinic that never gets an ad live gets nothing for its
          subscription and is the most likely to cancel. Each row is the first
          setup step still missing, with the link the owner needs. Meta is asked
          about the Lead Ads terms and payment method once a day.
        </p>
      </div>
      {onMinDays ? (
        <form onSubmit={submit} className="flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label htmlFor="stuck-min-days">No spend after (days)</Label>
            <Input
              id="stuck-min-days"
              type="number"
              min={0}
              max={365}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="w-24"
            />
          </div>
          <Button type="submit" variant="outline">
            Show
          </Button>
        </form>
      ) : null}
    </div>
  );
}

export function StuckSetupView({
  report,
  onMinDays,
}: {
  report: StuckClinicsReport;
  onMinDays?: (days: number | undefined) => void;
}) {
  return (
    <>
      <Header minDays={report.minDays} onMinDays={onMinDays} />
      <div className="flex flex-col gap-4">
        <p className="text-sm">
          <span className="font-semibold">
            {plural(report.clinics.length, 'clinic', 'clinics')} stuck
          </span>{' '}
          after {plural(report.minDays, 'day', 'days')} ·{' '}
          {report.noSpendClinics} of {report.payingClinics} paying clinics have
          no ad spend yet
        </p>
        {report.byStep.length ? (
          <ul className="flex flex-wrap gap-2" aria-label="Stuck by step">
            {report.byStep.map((s) => (
              <li key={s.step}>
                <Badge variant="secondary">
                  {s.label}: {s.count}
                </Badge>
              </li>
            ))}
          </ul>
        ) : null}
        {report.clinics.length ? (
          <StuckTable clinics={report.clinics} />
        ) : (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <PartyPopper />
              </EmptyMedia>
              <EmptyTitle>No clinic is stuck</EmptyTitle>
              <EmptyDescription>
                Every clinic paying for {plural(report.minDays, 'day', 'days')}{' '}
                or more has had ad spend.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
        <p className="text-muted-foreground text-xs">
          Read at {new Date(report.generatedAt).toLocaleTimeString('en-GB')}.
          Dots, left to right: Meta connected, ad account and Page, Lead Ads
          terms, privacy link, payment method, campaign created, campaign live,
          first spend, first lead. Grey means Meta has not answered yet.
        </p>
      </div>
    </>
  );
}

export function StuckSetupPage() {
  const [minDays, setMinDays] = useState<number | undefined>(undefined);
  const { data, isLoading, isError, error } = useStuckSetup(minDays);

  return (
    <div className="container max-w-6xl py-8">
      {isLoading ? (
        <>
          <Header />
          <Skeleton className="h-64 w-full" />
        </>
      ) : isError || !data ? (
        <>
          <Header />
          <Alert variant="destructive">
            <AlertTitle>Could not load the clinics stuck in setup</AlertTitle>
            <AlertDescription>
              {error?.message ?? 'The request failed.'}
            </AlertDescription>
          </Alert>
        </>
      ) : (
        <StuckSetupView report={data} onMinDays={setMinDays} />
      )}
    </div>
  );
}
